import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { policyCatalogKeys } from '../../api/policy-catalog-api'
import { policyOrderKeys } from '../../api/policy-order-api'
import { entityKeys } from '../../lib/entity-queries'
import { policyErrorMessage } from '../../lib/policy-authoring'
import {
  assistantPolicyApi,
  assistantPolicyKeys,
  type AssistantDefinition,
} from '../../api/assistant-policy-api'

const policyRefreshIntervalMs = 3000

export function useAssistantPolicyResult(entityId: string) {
  return useQuery({
    queryKey: assistantPolicyKeys.result(entityId),
    queryFn: () => assistantPolicyApi.result(entityId),
    refetchInterval: (query) => {
      const status = query.state.data?.refreshStatus
      return status === 'Pending' || status === 'Running' ? policyRefreshIntervalMs : false
    },
  })
}

export function useAssistantDefinition(entityId: string) {
  return useQuery({
    queryKey: assistantPolicyKeys.own(entityId),
    queryFn: () => assistantPolicyApi.own(entityId),
  })
}

export function useSetAssistantEnabled(entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ enabled, own, operationId }: { enabled: boolean; own: AssistantDefinition | null; operationId: string }) =>
      assistantPolicyApi.set(entityId, enabled, own, operationId),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: assistantPolicyKeys.result(entityId) }),
        queryClient.invalidateQueries({ queryKey: entityKeys.settings(entityId) }),
        queryClient.invalidateQueries({ queryKey: ['assistant-availability'] }),
        queryClient.invalidateQueries({ queryKey: policyCatalogKeys.all(entityId) }),
        queryClient.invalidateQueries({ queryKey: policyOrderKeys.all(entityId) }),
      ])
    },
  })
}

export function assistantSettingErrorMessage(error: unknown, operation: 'save' | 'load' = 'save') {
  return policyErrorMessage(error, 'ASSISTANT_POLICY', 'assistant', operation)
}
